import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api-client';
import { formatRelativeDate } from '@/lib/utils';

interface ContractItem {
  contractId: string;
  ownerId: string;
  ownerName?: string | null;
  ownerEmail?: string | null;
  title: string;
  status: string;
  statusLabel?: string;
  contractType?: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'uploaded', label: 'Enviado' },
  { value: 'analyzing', label: 'Analisando' },
  { value: 'analyzed', label: 'Analisado' },
  { value: 'in_review', label: 'Em revisão' },
  { value: 'reviewed', label: 'Revisado' },
  { value: 'archived', label: 'Arquivado' },
];

export function AdminContratos() {
  const [filter, setFilter] = useState<string>('');
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'contracts', filter],
    queryFn: () =>
      api.get<{ items: ContractItem[] }>(
        `/admin/contracts?limit=200${filter ? `&status=${filter}` : ''}`,
      ),
  });

  const items = data?.items ?? [];

  const filtered = useMemo(() => {
    if (!q) return items;
    const n = q.toLowerCase();
    return items.filter(
      (c) =>
        c.title?.toLowerCase().includes(n) ||
        c.contractType?.toLowerCase().includes(n) ||
        c.ownerName?.toLowerCase().includes(n) ||
        c.ownerEmail?.toLowerCase().includes(n) ||
        c.contractId.includes(q),
    );
  }, [items, q]);

  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const c of items) m[c.status] = (m[c.status] ?? 0) + 1;
    return m;
  }, [items]);

  return (
    <AdminShell>
      <div className="max-w-7xl">
        <h1 className="font-serif text-display-lg">Contratos</h1>
        <p className="text-muted-foreground mt-1">
          {filtered.length} contratos {filter ? 'com este status' : 'no total'}.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <Input
            placeholder="Buscar por título, cliente, tipo…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
          />
          <Button
            size="sm"
            variant={filter === '' ? 'accent' : 'outline'}
            onClick={() => setFilter('')}
          >
            Todos {items.length > 0 && `(${items.length})`}
          </Button>
          {STATUS_OPTIONS.map((s) => (
            <Button
              key={s.value}
              size="sm"
              variant={filter === s.value ? 'accent' : 'outline'}
              onClick={() => setFilter(s.value)}
            >
              {s.label} {counts[s.value] ? `(${counts[s.value]})` : ''}
            </Button>
          ))}
        </div>

        <Card className="mt-6">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60 text-left text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Contrato</th>
                  <th className="p-3 font-medium">Cliente</th>
                  <th className="p-3 font-medium">Tipo</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium text-right">Tamanho</th>
                  <th className="p-3 font-medium text-right">Enviado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {isLoading && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground">
                      Carregando…
                    </td>
                  </tr>
                )}
                {!isLoading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground">
                      {filter
                        ? `Nenhum contrato com status "${labelFor(filter)}".`
                        : 'Nenhum contrato enviado ainda.'}
                    </td>
                  </tr>
                )}
                {filtered.map((c) => (
                  <tr key={c.contractId} className="hover:bg-muted/30 transition-colors">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground flex-none" />
                        <span className="font-medium truncate">{c.title}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                        {c.contractId.slice(0, 12)}…
                      </p>
                    </td>
                    <td className="p-3">
                      <p className="font-medium">{c.ownerName ?? '—'}</p>
                      <p className="text-[11px] text-muted-foreground">{c.ownerEmail ?? ''}</p>
                    </td>
                    <td className="p-3 text-muted-foreground text-xs">{c.contractType ?? '—'}</td>
                    <td className="p-3">
                      <Badge variant={statusVariant(c.status)}>
                        {c.statusLabel ?? labelFor(c.status)}
                      </Badge>
                    </td>
                    <td className="p-3 text-right text-xs text-muted-foreground">
                      {(c.fileSize / 1024).toFixed(0)} KB
                    </td>
                    <td className="p-3 text-right text-xs text-muted-foreground">
                      {formatRelativeDate(c.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}

function labelFor(status: string): string {
  return STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status;
}

function statusVariant(s: string): 'default' | 'success' | 'warning' | 'danger' | 'muted' {
  if (s === 'reviewed' || s === 'analyzed') return 'success';
  if (s === 'in_review' || s === 'analyzing' || s === 'uploaded') return 'warning';
  if (s === 'archived') return 'muted';
  return 'default';
}
